import React, { useState, useEffect } from "react";
import {
    FaTicketAlt,
    FaExclamationTriangle,
    FaCheckCircle,
    FaClock,
    FaUsers,
    FaComments,
    FaStar,
    FaArrowUp,
    FaArrowDown,
    FaChartBar,
    FaFilter,
    FaDownload
} from "react-icons/fa";

const SuperAdminSupportDashboard = () => {
    const [timeRange, setTimeRange] = useState("today");
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const stats = {
        totalTickets: 1245,
        openTickets: 89,
        resolvedToday: 42,
        avgResponseTime: "15m",
        customerSatisfaction: "4.8",
        liveChats: 23,
        agentsOnline: 8,
        escalationRate: "12%",
        firstContactResolution: "85%",
        slaCompliance: "92%"
    };

    const recentActivities = [
        { id: 1, type: "ticket", action: "Ticket #TICK-789456 resolved", agent: "Agent Smith", time: "10 min ago" },
        { id: 2, type: "chat", action: "Live chat session ended", agent: "Agent Johnson", time: "25 min ago" },
        { id: 3, type: "escalation", action: "Ticket #TICK-123789 escalated", agent: "System", time: "1 hour ago" },
        { id: 4, type: "article", action: "New article published", agent: "Admin", time: "2 hours ago" },
        { id: 5, type: "agent", action: "Agent Davis went online", agent: "System", time: "3 hours ago" }
    ];

    const topIssues = [
        { issue: "Late Delivery", count: 156, trend: "+12%" },
        { issue: "Payment Failed", count: 134, trend: "-5%" },
        { issue: "Wrong Item", count: 89, trend: "+8%" },
        { issue: "App Crash", count: 67, trend: "+15%" },
        { issue: "Refund Delay", count: 45, trend: "-3%" }
    ];

    const agentPerformance = [
        { name: "Agent Smith", tickets: 42, avgRating: 4.8, responseTime: "10m", satisfaction: "95%" },
        { name: "Agent Johnson", tickets: 38, avgRating: 4.9, responseTime: "8m", satisfaction: "98%" },
        { name: "Agent Williams", tickets: 35, avgRating: 4.7, responseTime: "15m", satisfaction: "92%" },
        { name: "Agent Davis", tickets: 28, avgRating: 4.6, responseTime: "20m", satisfaction: "88%" }
    ];

    return (
        <div style={{
            padding: isMobile ? "15px" : "20px",
            fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
            borderRadius: "10px",
            maxWidth: "100%",
            overflowX: "hidden"
        }} className="dashboard-res">
            {/* Header */}
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: isMobile ? "20px" : "30px",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? "15px" : "20px"
            }}>
                <div style={{ width: isMobile ? "100%" : "auto" }}>
                    <h1 style={{
                        fontSize: isMobile ? "22px" : "28px",
                        fontWeight: "700",
                        color: "#2c3e50",
                        margin: "0 0 5px 0",
                        lineHeight: isMobile ? "1.3" : "1.2"
                    }}>
                        Support System Dashboard
                    </h1>
                    <p style={{ 
                        color: "#6c757d", 
                        margin: "0",
                        fontSize: isMobile ? "14px" : "16px"
                    }}>
                        Real-time overview of support operations and performance metrics
                    </p>
                </div>
                <div style={{
                    display: "flex",
                    gap: isMobile ? "10px" : "15px",
                    alignItems: "center",
                    width: isMobile ? "100%" : "auto",
                    flexDirection: isMobile ? "column" : "row"
                }}>
                    <div style={{
                        display: "flex",
                        gap: "5px",
                        backgroundColor: "#fff",
                        borderRadius: "5px",
                        overflow: "hidden",
                        border: "1px solid #ddd",
                        width: isMobile ? "100%" : "auto",
                        justifyContent: "stretch"
                    }}>
                        {['today', 'week', 'month', 'quarter'].map((period) => (
                            <button
                                key={period}
                                style={{
                                    padding: isMobile ? "8px 10px" : "8px 15px",
                                    border: "none",
                                    backgroundColor: timeRange === period ? "#6a0dad" : "transparent",
                                    color: timeRange === period ? "white" : "#495057",
                                    cursor: "pointer",
                                    fontSize: isMobile ? "12px" : "14px",
                                    transition: "all 0.3s",
                                    flex: 1,
                                    textAlign: "center",
                                    whiteSpace: "nowrap",
                                    minWidth: isMobile ? "60px" : "auto"
                                }}
                                onClick={() => setTimeRange(period)}
                            >
                                {isMobile ? period.charAt(0).toUpperCase() : period.charAt(0).toUpperCase() + period.slice(1)}
                            </button>
                        ))}
                    </div>
                    <button style={{
                        padding: isMobile ? "8px 12px" : "8px 15px",
                        backgroundColor: "#6a0dad",
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: isMobile ? "5px" : "8px",
                        fontSize: isMobile ? "13px" : "14px",
                        fontWeight: "500",
                        width: isMobile ? "100%" : "auto",
                        minHeight: "36px"
                    }}>
                        <FaDownload style={{ fontSize: isMobile ? "12px" : "14px" }} /> 
                        {isMobile ? "Export" : "Export Report"}
                    </button>
                </div>
            </div>

            {/* Stats Grid - Mobile optimized */}
            <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(auto-fit, minmax(250px, 1fr))",
                gap: isMobile ? "10px" : "20px",
                marginBottom: isMobile ? "20px" : "30px"
            }}>
                {[
                    { key: 'totalTickets', label: 'Total Tickets', icon: <FaTicketAlt />, color: '#667eea', trend: "+12%" },
                    { key: 'openTickets', label: 'Open Tickets', icon: <FaExclamationTriangle />, color: '#ff6b6b', trend: "-3%" },
                    { key: 'resolvedToday', label: 'Resolved Today', icon: <FaCheckCircle />, color: '#28a745', trend: "+8%" },
                    { key: 'avgResponseTime', label: 'Avg Response', icon: <FaClock />, color: '#ffc107', trend: "-2m" },
                    { key: 'customerSatisfaction', label: 'Satisfaction', icon: <FaStar />, color: '#9d4edd', trend: "+0.2" },
                    { key: 'agentsOnline', label: 'Agents Online', icon: <FaUsers />, color: '#06d6a0', trend: "+2" }
                ].map((stat) => (
                    <div
                        key={stat.key}
                        style={{
                            backgroundColor: "#fff",
                            borderRadius: isMobile ? "8px" : "10px",
                            padding: isMobile ? "15px" : "20px",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                            display: "flex",
                            alignItems: "center",
                            gap: isMobile ? "10px" : "20px",
                            transition: "all 0.3s",
                            cursor: "pointer",
                            minHeight: isMobile ? "90px" : "auto"
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-5px)";
                            e.currentTarget.style.boxShadow = "0 5px 20px rgba(0,0,0,0.15)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.1)";
                        }}
                    >
                        <div style={{
                            width: isMobile ? "40px" : "60px",
                            height: isMobile ? "40px" : "60px",
                            borderRadius: isMobile ? "8px" : "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: isMobile ? "18px" : "24px",
                            backgroundColor: stat.color + "20",
                            color: stat.color,
                            flexShrink: 0
                        }}>
                            {stat.icon}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                                fontSize: isMobile ? "22px" : "28px",
                                fontWeight: "bold",
                                color: "#2c3e50",
                                marginBottom: isMobile ? "3px" : "5px",
                                lineHeight: "1"
                            }}>
                                {stats[stat.key]}
                            </div>
                            <div style={{
                                fontSize: isMobile ? "11px" : "14px",
                                color: "#6c757d",
                                marginBottom: isMobile ? "3px" : "5px",
                                whiteSpace: isMobile ? "normal" : "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                            }}>
                                {stat.label}
                            </div>
                            <div style={{
                                fontSize: isMobile ? "10px" : "12px",
                                display: "flex",
                                alignItems: "center",
                                gap: "3px",
                                fontWeight: "500",
                                color: stat.trend.startsWith('+') || stat.trend.endsWith('m') ? "#28a745" : "#dc3545"
                            }}>
                                {stat.trend.startsWith('+') ? 
                                    <FaArrowUp style={{ fontSize: isMobile ? "8px" : "10px" }} /> : 
                                    <FaArrowDown style={{ fontSize: isMobile ? "8px" : "10px" }} />
                                }
                                {stat.trend}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Main Content Grid - Mobile optimized */}
            <div style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? "15px" : "20px"
            }}>
                {/* Left Column */}
                <div style={{ flex: isMobile ? "none" : 2 }}>
                    {/* Performance Metrics - Mobile optimized */}
                    <div style={{
                        backgroundColor: "#fff",
                        borderRadius: isMobile ? "8px" : "10px",
                        padding: isMobile ? "15px" : "20px",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                        marginBottom: isMobile ? "15px" : "20px"
                    }}>
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: isMobile ? "15px" : "20px",
                            flexDirection: isMobile ? "column" : "row",
                            gap: isMobile ? "10px" : "0",
                            alignItems: isMobile ? "flex-start" : "center"
                        }}>
                            <h3 style={{
                                fontSize: isMobile ? "16px" : "18px",
                                fontWeight: "600",
                                color: "#2c3e50",
                                margin: "0",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                            }}>
                                <FaChartBar style={{ fontSize: isMobile ? "14px" : "16px" }} /> 
                                Performance Metrics
                            </h3>
                            <select style={{
                                padding: isMobile ? "6px 10px" : "8px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "5px",
                                fontSize: isMobile ? "12px" : "14px",
                                backgroundColor: "#fff",
                                width: isMobile ? "100%" : "auto",
                                minWidth: isMobile ? "100%" : "150px"
                            }}>
                                <option>All Metrics</option>
                                <option>Response Time</option>
                                <option>Satisfaction</option>
                                <option>Resolution Rate</option>
                            </select>
                        </div>
                        <div style={{ 
                            display: "grid", 
                            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", 
                            gap: isMobile ? "10px" : "15px" 
                        }}>
                            {[
                                { name: "First Contact Resolution", value: "85%", target: "90%", color: "#28a745" },
                                { name: "SLA Compliance", value: "92%", target: "95%", color: "#667eea" },
                                { name: "Escalation Rate", value: "12%", target: "10%", color: "#fd7e14" },
                                { name: "CSAT Score", value: "4.8", target: "4.5", color: "#9d4edd" }
                            ].map((metric, index) => (
                                <div key={index} style={{
                                    backgroundColor: "#fff",
                                    borderRadius: "8px",
                                    padding: isMobile ? "12px" : "15px",
                                    marginBottom: isMobile ? "0" : "10px",
                                    border: "1px solid #e9ecef"
                                }}>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: "8px"
                                    }}>
                                        <div style={{
                                            fontSize: isMobile ? "12px" : "14px",
                                            fontWeight: "500",
                                            color: "#495057",
                                            lineHeight: "1.3"
                                        }}>
                                            {isMobile && metric.name.length > 20 ? metric.name.substring(0, 20) + "..." : metric.name}
                                        </div>
                                        <div style={{
                                            fontSize: isMobile ? "16px" : "18px",
                                            fontWeight: "bold",
                                            color: "#2c3e50"
                                        }}>
                                            {metric.value}
                                        </div>
                                    </div>
                                    <div style={{
                                        height: "6px",
                                        backgroundColor: "#e9ecef",
                                        borderRadius: "4px",
                                        overflow: "hidden",
                                        marginTop: "5px"
                                    }}>
                                        <div style={{
                                            height: "100%",
                                            borderRadius: "4px",
                                            width: metric.value.replace('%', ''),
                                            backgroundColor: metric.color
                                        }}></div>
                                    </div>
                                    <div style={{ 
                                        fontSize: isMobile ? "10px" : "12px", 
                                        color: "#6c757d", 
                                        marginTop: "5px" 
                                    }}>
                                        Target: {metric.target}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Top Issues - Mobile optimized */}
                    <div style={{
                        backgroundColor: "#fff",
                        borderRadius: isMobile ? "8px" : "10px",
                        padding: isMobile ? "15px" : "20px",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                    }}>
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: isMobile ? "15px" : "20px",
                            flexDirection: isMobile ? "column" : "row",
                            gap: isMobile ? "8px" : "0",
                            alignItems: isMobile ? "flex-start" : "center"
                        }}>
                            <h3 style={{
                                fontSize: isMobile ? "16px" : "18px",
                                fontWeight: "600",
                                color: "#2c3e50",
                                margin: "0",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                            }}>
                                <FaExclamationTriangle style={{ fontSize: isMobile ? "14px" : "16px" }} /> 
                                Top Issues
                            </h3>
                            <span style={{ 
                                fontSize: isMobile ? "12px" : "14px", 
                                color: "#6c757d",
                                alignSelf: isMobile ? "flex-start" : "center"
                            }}>
                                Last 7 days
                            </span>
                        </div>
                        <div style={{ 
                            overflowX: "auto",
                            WebkitOverflowScrolling: "touch",
                            msOverflowStyle: "-ms-autohiding-scrollbar"
                        }}>
                            <table style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                minWidth: isMobile ? "500px" : "auto"
                            }}>
                                <thead>
                                    <tr>
                                        <th style={{
                                            padding: isMobile ? "8px 10px" : "12px",
                                            textAlign: "left",
                                            fontWeight: "600",
                                            color: "#495057",
                                            borderBottom: "2px solid #e9ecef",
                                            backgroundColor: "#f8f9fa",
                                            fontSize: isMobile ? "12px" : "14px",
                                            whiteSpace: "nowrap"
                                        }}>
                                            Issue Type
                                        </th>
                                        <th style={{
                                            padding: isMobile ? "8px 10px" : "12px",
                                            textAlign: "left",
                                            fontWeight: "600",
                                            color: "#495057",
                                            borderBottom: "2px solid #e9ecef",
                                            backgroundColor: "#f8f9fa",
                                            fontSize: isMobile ? "12px" : "14px",
                                            whiteSpace: "nowrap"
                                        }}>
                                            Count
                                        </th>
                                        <th style={{
                                            padding: isMobile ? "8px 10px" : "12px",
                                            textAlign: "left",
                                            fontWeight: "600",
                                            color: "#495057",
                                            borderBottom: "2px solid #e9ecef",
                                            backgroundColor: "#f8f9fa",
                                            fontSize: isMobile ? "12px" : "14px",
                                            whiteSpace: "nowrap"
                                        }}>
                                            Trend
                                        </th>
                                        <th style={{
                                            padding: isMobile ? "8px 10px" : "12px",
                                            textAlign: "left",
                                            fontWeight: "600",
                                            color: "#495057",
                                            borderBottom: "2px solid #e9ecef",
                                            backgroundColor: "#f8f9fa",
                                            fontSize: isMobile ? "12px" : "14px",
                                            whiteSpace: "nowrap"
                                        }}>
                                            Priority
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topIssues.map((issue, index) => (
                                        <tr key={index}>
                                            <td style={{
                                                padding: isMobile ? "8px 10px" : "12px",
                                                borderBottom: "1px solid #e9ecef",
                                                verticalAlign: "middle",
                                                fontSize: isMobile ? "12px" : "14px",
                                                whiteSpace: "nowrap"
                                            }}>
                                                <div style={{ fontWeight: "500" }}>{issue.issue}</div>
                                            </td>
                                            <td style={{
                                                padding: isMobile ? "8px 10px" : "12px",
                                                borderBottom: "1px solid #e9ecef",
                                                verticalAlign: "middle",
                                                fontSize: isMobile ? "12px" : "14px"
                                            }}>
                                                <div style={{ fontWeight: "600", color: "#2c3e50" }}>{issue.count}</div>
                                            </td>
                                            <td style={{
                                                padding: isMobile ? "8px 10px" : "12px",
                                                borderBottom: "1px solid #e9ecef",
                                                verticalAlign: "middle",
                                                fontSize: isMobile ? "12px" : "14px"
                                            }}>
                                                <span style={{
                                                    color: issue.trend.startsWith('+') ? "#28a745" : "#dc3545",
                                                    fontWeight: "600",
                                                    fontSize: isMobile ? "11px" : "13px"
                                                }}>
                                                    {issue.trend}
                                                </span>
                                            </td>
                                            <td style={{
                                                padding: isMobile ? "8px 10px" : "12px",
                                                borderBottom: "1px solid #e9ecef",
                                                verticalAlign: "middle",
                                                fontSize: isMobile ? "12px" : "14px"
                                            }}>
                                                <span style={{
                                                    padding: isMobile ? "3px 8px" : "4px 12px",
                                                    borderRadius: "20px",
                                                    fontSize: isMobile ? "10px" : "12px",
                                                    fontWeight: "600",
                                                    backgroundColor: index === 0 ? "#dc354520" : index < 3 ? "#ffc10720" : "#28a74520",
                                                    color: index === 0 ? "#dc3545" : index < 3 ? "#ffc107" : "#28a745",
                                                    display: "inline-block",
                                                    textAlign: "center",
                                                    minWidth: "50px"
                                                }}>
                                                    {index === 0 ? 'High' : index < 3 ? 'Medium' : 'Low'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div style={{ flex: isMobile ? "none" : 1 }}>
                    {/* Recent Activities - Mobile optimized */}
                    <div style={{
                        backgroundColor: "#fff",
                        borderRadius: isMobile ? "8px" : "10px",
                        padding: isMobile ? "15px" : "20px",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                        marginBottom: isMobile ? "15px" : "20px"
                    }}>
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: isMobile ? "15px" : "20px",
                            flexDirection: isMobile ? "column" : "row",
                            gap: isMobile ? "10px" : "0",
                            alignItems: isMobile ? "flex-start" : "center"
                        }}>
                            <h3 style={{
                                fontSize: isMobile ? "16px" : "18px",
                                fontWeight: "600",
                                color: "#2c3e50",
                                margin: "0",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                            }}>
                                <FaClock style={{ fontSize: isMobile ? "14px" : "16px" }} /> 
                                Recent Activities
                            </h3>
                            <button style={{
                                padding: isMobile ? "5px 10px" : "6px 12px",
                                backgroundColor: "transparent",
                                color: "#6a0dad",
                                border: "1px solid #6a0dad",
                                borderRadius: "5px",
                                cursor: "pointer",
                                fontSize: isMobile ? "11px" : "12px",
                                fontWeight: "500",
                                alignSelf: isMobile ? "flex-start" : "center"
                            }}>
                                View All
                            </button>
                        </div>
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: isMobile ? "10px" : "15px"
                        }}>
                            {recentActivities.map((activity) => (
                                <div key={activity.id} style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: isMobile ? "10px" : "15px",
                                    padding: isMobile ? "12px" : "15px",
                                    borderRadius: "8px",
                                    backgroundColor: "#f8f9fa",
                                    transition: "background-color 0.3s"
                                }}>
                                    <div style={{
                                        width: isMobile ? "35px" : "40px",
                                        height: isMobile ? "35px" : "40px",
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: isMobile ? "14px" : "16px",
                                        color: "white",
                                        backgroundColor: activity.type === 'ticket' ? "#667eea" :
                                                       activity.type === 'chat' ? "#28a745" :
                                                       activity.type === 'escalation' ? "#fd7e14" :
                                                       activity.type === 'article' ? "#9d4edd" : "#06d6a0",
                                        flexShrink: 0
                                    }}>
                                        {activity.type === 'ticket' ? '🎫' :
                                         activity.type === 'chat' ? '💬' :
                                         activity.type === 'escalation' ? '⚠️' :
                                         activity.type === 'article' ? '📚' : '👤'}
                                    </div>
                                    <div style={{ 
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: "hidden"
                                    }}>
                                        <div style={{
                                            fontSize: isMobile ? "12px" : "14px",
                                            color: "#2c3e50",
                                            marginBottom: "3px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis"
                                        }}>
                                            {activity.action}
                                        </div>
                                        <div style={{
                                            fontSize: isMobile ? "10px" : "12px",
                                            color: "#6c757d",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis"
                                        }}>
                                            By {activity.agent} • {activity.time}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Agent Performance - Mobile optimized */}
                    <div style={{
                        backgroundColor: "#fff",
                        borderRadius: isMobile ? "8px" : "10px",
                        padding: isMobile ? "15px" : "20px",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                    }}>
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: isMobile ? "15px" : "20px",
                            flexDirection: isMobile ? "column" : "row",
                            gap: isMobile ? "8px" : "0",
                            alignItems: isMobile ? "flex-start" : "center"
                        }}>
                            <h3 style={{
                                fontSize: isMobile ? "16px" : "18px",
                                fontWeight: "600",
                                color: "#2c3e50",
                                margin: "0",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                            }}>
                                <FaUsers style={{ fontSize: isMobile ? "14px" : "16px" }} /> 
                                Top Performers
                            </h3>
                            <span style={{ 
                                fontSize: isMobile ? "11px" : "12px", 
                                color: "#28a745",
                                alignSelf: isMobile ? "flex-start" : "center"
                            }}>
                                {agentPerformance.filter(a => parseFloat(a.satisfaction) > 90).length}/{agentPerformance.length} above target
                            </span>
                        </div>
                        <div style={{ 
                            display: "flex", 
                            flexDirection: "column", 
                            gap: isMobile ? "8px" : "10px" 
                        }}>
                            {agentPerformance.map((agent, index) => (
                                <div key={index} style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: isMobile ? "10px" : "15px",
                                    padding: isMobile ? "8px" : "10px",
                                    backgroundColor: "#f8f9fa",
                                    borderRadius: "8px"
                                }}>
                                    <div style={{
                                        width: isMobile ? "35px" : "40px",
                                        height: isMobile ? "35px" : "40px",
                                        borderRadius: "50%",
                                        backgroundColor: "#667eea",
                                        color: "white",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: "600",
                                        fontSize: isMobile ? "14px" : "16px",
                                        flexShrink: 0
                                    }}>
                                        {agent.name.split(' ')[1][0]}
                                    </div>
                                    <div style={{ 
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: "hidden"
                                    }}>
                                        <div style={{ 
                                            fontWeight: "500", 
                                            color: "#2c3e50",
                                            fontSize: isMobile ? "13px" : "14px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis"
                                        }}>
                                            {agent.name}
                                        </div>
                                        <div style={{ 
                                            fontSize: isMobile ? "10px" : "12px", 
                                            color: "#6c757d",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis"
                                        }}>
                                            {agent.tickets} tickets • {agent.responseTime} avg
                                        </div>
                                    </div>
                                    <div style={{ 
                                        textAlign: "right",
                                        flexShrink: 0
                                    }}>
                                        <div style={{ 
                                            fontSize: isMobile ? "14px" : "16px", 
                                            fontWeight: "600", 
                                            color: "#ffc107" 
                                        }}>
                                            {agent.avgRating} ★
                                        </div>
                                        <div style={{ 
                                            fontSize: isMobile ? "10px" : "11px", 
                                            color: "#28a745" 
                                        }}>
                                            {agent.satisfaction} satisfied
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SuperAdminSupportDashboard;